<script>
    import { clickOutside } from "$/utils/clickOutside.js";
    import { tooltip } from "$/utils/tooltip";
    import {
        ActiveTopicStore,
        ActiveTopic,
        normalizeUrl,
    } from "$/stores/extension/ActiveTopicStore";
    import { get } from "svelte/store";
    import { addHttpsToUrl } from "$/utils/Utils";

    export let editLink = false;
    export let card;
    export let pageUrl;
    let updateSameUrlSnippets = false;
    let errorMessage = "";
    let singleSnippetForUrl = true;
    let sameUrlSnippets = [];
    let fullUrl = addHttpsToUrl(pageUrl);
    let oldValue = pageUrl;

    $: {
        const topic = get(ActiveTopic);
        const updatedSnippet = topic?.snippets.find((s) => s.id === card.id);
        sameUrlSnippets = updatedSnippet
            ? topic.snippets.filter(
                  (s) =>
                      normalizeUrl(s.urlPath) ===
                          normalizeUrl(updatedSnippet.urlPath) &&
                      s.id !== updatedSnippet.id,
              )
            : [];
        singleSnippetForUrl = sameUrlSnippets.length === 0;
    }

    function validateUrl() {
        try {
            pageUrl = pageUrl.trim();
            fullUrl = addHttpsToUrl(pageUrl);
            new URL(fullUrl);
            errorMessage = "";
        } catch {
            errorMessage = "Invalid URL";
        }
    }

    function saveNewUrl() {
        if (errorMessage) return;

        editLink = false;

        if (addHttpsToUrl(oldValue) === addHttpsToUrl(fullUrl)) {
            updateSameUrlSnippets = false;
            return;
        }

        oldValue = fullUrl;

        const snippetsToUpdate = updateSameUrlSnippets
            ? sameUrlSnippets
                  .map((snippet) => ({ cardId: snippet.id }))
                  .concat({ cardId: card.id })
            : [{ cardId: card.id }];

        const snippetIds = snippetsToUpdate.map((snippet) => snippet.cardId);
        ActiveTopicStore.updateSnippetUrls(snippetIds, fullUrl);

        updateSameUrlSnippets = false;
    }

    function extractPlainText(htmlText) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, "text/html");
        return doc.body.textContent || doc.body.innerText;
    }

    function trimText(name, content, maxChars) {
        const extractText = (text) => {
            return text.length > maxChars
                ? text.slice(0, maxChars) + "..."
                : text;
        };

        if (content && typeof content === "string") {
            const trimmedContent = content.trim();
            const isImageOnly =
                trimmedContent.startsWith("<img") &&
                trimmedContent.endsWith(">") &&
                !extractPlainText(trimmedContent);
            if (isImageOnly) {
                return "Image Content";
            }
        }

        return name
            ? extractText(name)
            : extractText(extractPlainText(content));
    }
</script>

<div
    class="edit-link"
    use:clickOutside
    on:click_outside={() => (editLink = false)}
>
    {#if editLink}
        <div
            class="edit-link-menu"
            draggable="false"
            on:dragstart|stopPropagation|preventDefault
            on:drag|stopPropagation|preventDefault
            on:dragend|stopPropagation|preventDefault
        >
            <div class="input-container {errorMessage ? 'error' : ''}">
                <input
                    type="text"
                    bind:value={pageUrl}
                    class="edit-link-input"
                    on:input={validateUrl}
                    on:keydown={(e) => {
                        if (e.key === "Enter") saveNewUrl();
                    }}
                    autofocus
                />
                {#if errorMessage}
                    <svg
                        class="invalid-link"
                        use:tooltip={errorMessage}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        on:click|stopPropagation
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                        />
                    </svg>
                {/if}
            </div>
            {#if !singleSnippetForUrl}
                <label class="edit-link-checkbox">
                    <input type="checkbox" bind:checked={updateSameUrlSnippets} />
                    Update snippets with the same URL
                </label>
                {#each sameUrlSnippets.slice(0, 3) as snippet}
                    <div class="same-url-snippets">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="size-6"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                            />
                        </svg>
                        <div class="snippet-title">
                            {trimText(snippet.name, snippet.content, 35)}
                        </div>
                    </div>
                {/each}
                <!-- 
                    HACK - This is a UX hack because there's a problem with dropdown going under cards because of a way we
                    handle comments and stickiness in the extension. All dropdowns should use portals to prevent this from
                    happening but as of time of writing this we're not using any
                -->
                {#if sameUrlSnippets.length > 3}
                    <div class="more-url-snippets">
                        <span
                            use:tooltip={sameUrlSnippets
                                .slice(3)
                                .map((snippet) =>
                                    trimText(snippet.name, snippet.content, 30),
                                )
                                .join("<br>")}
                        >
                            ... and {sameUrlSnippets.length - 3} more
                        </span>
                    </div>
                {/if}
            {/if}
            <div class="edit-link-actions">
                <button
                    class="edit-link-item"
                    on:click={() => (editLink = false)}
                    >Cancel
                </button>
                <button class="edit-link-item" on:click={saveNewUrl}
                    >Save
                </button>
            </div>
        </div>
    {/if}
</div>

<style>
    .edit-link {
        @apply reset relative z-10 flex !important;
    }

    .edit-link::before {
        @apply hidden !important;
    }

    .edit-link-menu {
        @apply reset absolute -top-4 right-2 z-50 flex w-[280px] flex-col rounded-md border border-solid border-gray-200 bg-white p-1 shadow-md !important;
    }

    .input-container {
        @apply reset relative flex w-full bg-gray-100 !important;
    }

    .input-container.error {
        @apply bg-pink-100 !important;
    }

    .invalid-link {
        @apply reset absolute right-2 top-1/2 h-5 w-5 -translate-y-1/2 transform text-pink-500 !important;
    }

    .edit-link-input {
        @apply reset relative grow bg-gray-100 px-2 py-1 text-xs focus:outline-none !important;
    }

    .edit-link-checkbox {
        @apply mb-2 mt-2 flex items-center pl-2 text-xs !important;
    }

    .edit-link-checkbox input {
        @apply relative mr-2 h-3 w-3 cursor-pointer appearance-none rounded-sm border border-gray-500 bg-white !important;
        border: 1px solid #6b7280 !important;
    }

    .edit-link-checkbox input:checked {
        @apply bg-sky-400 !important;
        border: 1px solid #6b7280 !important;
    }

    .edit-link-checkbox input:checked::after {
        content: "\2713";
        @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-2xs text-white !important;
    }

    .edit-link-actions {
        @apply reset mb-1 mt-3 flex justify-end !important;
    }

    .edit-link-item {
        @apply reset flex cursor-pointer items-center justify-center whitespace-nowrap rounded px-2 py-1 text-xs hover:bg-gray-200 !important;
    }

    .snippet-title {
        @apply reset w-full text-xs font-medium !important;
    }

    .same-url-snippets {
        @apply reset mb-2 flex items-center space-x-2 bg-gray-100 p-2 !important;
    }

    .same-url-snippets svg {
        @apply reset h-3 w-3 !important;
    }

    .more-url-snippets {
        @apply reset px-2 text-right text-xs text-gray-600 !important;
    }
</style>
